import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Helper to get system preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, user, updateUserPreferences }) => {
  // themePreference: 'system' | 'light' | 'dark'
  const [themePreference, setThemePreference] = useState(() => {
    // If user is logged in, use their preference
    if (user?.theme_preference) {
      return user.theme_preference;
    }
    // Otherwise, use localStorage (for non-logged users)
    const saved = localStorage.getItem('viajejunto-theme-preference');
    // Migration: if old format exists, migrate it
    if (!saved) {
      const oldTheme = localStorage.getItem('viajejunto-theme');
      if (oldTheme === 'light' || oldTheme === 'dark') {
        return oldTheme;
      }
    }
    return saved || 'system';
  });

  // resolvedTheme: actual theme being applied ('light' | 'dark')
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    if (themePreference === 'system') {
      return getSystemTheme();
    }
    return themePreference;
  });

  // Sync with user preferences when user changes (login/logout)
  useEffect(() => {
    if (user?.theme_preference) {
      setThemePreference(user.theme_preference);
    }
  }, [user?.theme_preference]);

  // Update resolved theme when preference or system changes
  const updateResolvedTheme = useCallback(() => {
    if (themePreference === 'system') {
      setResolvedTheme(getSystemTheme());
    } else {
      setResolvedTheme(themePreference);
    }
  }, [themePreference]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (themePreference === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themePreference]);

  // Apply theme to document and save preference
  useEffect(() => {
    updateResolvedTheme();
    localStorage.setItem('viajejunto-theme-preference', themePreference);
    // Remove old format
    localStorage.removeItem('viajejunto-theme');
  }, [themePreference, updateResolvedTheme]);

  // Apply resolved theme to DOM
  useEffect(() => {
    if (resolvedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [resolvedTheme]);

  const setTheme = useCallback((newTheme) => {
    if (['system', 'light', 'dark'].includes(newTheme)) {
      setThemePreference(newTheme);
      
      // If user is logged in, save to backend
      if (updateUserPreferences) {
        updateUserPreferences({ theme_preference: newTheme }).catch(err => {
          console.error('Error saving theme preference:', err);
        });
      }
    }
  }, [updateUserPreferences]);

  // Legacy toggle (cycles through: light -> dark -> system -> light)
  const toggleTheme = useCallback(() => {
    const newTheme = themePreference === 'light' ? 'dark' : themePreference === 'dark' ? 'system' : 'light';
    setTheme(newTheme);
  }, [themePreference, setTheme]);

  const setLightTheme = useCallback(() => setTheme('light'), [setTheme]);
  const setDarkTheme = useCallback(() => setTheme('dark'), [setTheme]);
  const setSystemTheme = useCallback(() => setTheme('system'), [setTheme]);

  return (
    <ThemeContext.Provider value={{ 
      theme: resolvedTheme,
      themePreference,
      setTheme,
      toggleTheme, 
      setLightTheme, 
      setDarkTheme,
      setSystemTheme,
      isDark: resolvedTheme === 'dark',
      isSystem: themePreference === 'system'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
